import { ShieldAlert } from 'lucide-react';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { MOTIVO_EMERGENCIA_MAX, MOTIVO_EMERGENCIA_MIN, type ResultadoEmergencia } from './accesoEmergencia';
import type { CodigoAccesoDenegado } from './accesoDenegado';

/**
 * MT-042: lo que ve el profesional cuando el servidor niega abrir un expediente. El intento ya quedó en la bitácora de accesos. Si el servidor dice que puede
 * declarar una emergencia (break-glass), se ofrece el botón «Acceso de emergencia», que exige un motivo.
 */
export function AvisoAccesoDenegado({
  codigo,
  onVolver,
  puedeEmergencia = false,
  onDeclarar,
  onAbierto,
}: {
  codigo: CodigoAccesoDenegado | null;
  onVolver: () => void;
  puedeEmergencia?: boolean;
  onDeclarar?: (motivo: string) => Promise<ResultadoEmergencia>;
  onAbierto?: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [formulario, setFormulario] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [abierto, setAbierto] = useState<{ horas: number; hasta: number } | null>(null);
  const motivoValido = motivo.trim().length >= MOTIVO_EMERGENCIA_MIN;

  const enviar = async (): Promise<void> => {
    if (!onDeclarar || !motivoValido) return;
    setEnviando(true);
    setError(null);
    const r = await onDeclarar(motivo);
    setEnviando(false);
    if (r.ok) setAbierto({ horas: r.horas, hasta: r.hasta });
    else setError(r.error);
  };

  // Tras el éxito se muestra cuánto dura el acceso y recién después se abre el expediente (sin salto brusco).
  useEffect(() => {
    if (!abierto) return;
    const id = setTimeout(() => onAbierto?.(), 2500);
    return () => clearTimeout(id);
  }, [abierto, onAbierto]);

  return (
    <div className="mx-auto max-w-2xl py-8" data-testid="aviso-acceso-denegado">
      <Alert variant="red">
        <ShieldAlert />
        <AlertTitle>{t('accesoDenegado.titulo')}</AlertTitle>
        <AlertDescription>
          <p>{t(codigo ? `accesoDenegado.${codigo}` : 'accesoDenegado.generico')}</p>
          <p>{t('accesoDenegado.registrado')}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onVolver}>
              {t('accesoDenegado.volver')}
            </Button>
            {puedeEmergencia && onDeclarar && !formulario ? (
              <Button size="sm" variant="destructive" onClick={() => setFormulario(true)}>
                {t('accesoDenegado.emergencia.boton')}
              </Button>
            ) : null}
          </div>
          {abierto ? (
            <p role="status" data-testid="emergencia-abierta">
              {t('accesoDenegado.emergencia.abierto', { horas: abierto.horas, hasta: new Date(abierto.hasta).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' }) })}
            </p>
          ) : null}
          {puedeEmergencia && onDeclarar && formulario && !abierto ? (
            <div className="grid w-full gap-2" data-testid="formulario-emergencia">
              <p>{t('accesoDenegado.emergencia.explicacion')}</p>
              <Textarea
                aria-label={t('accesoDenegado.emergencia.motivo')}
                placeholder={t('accesoDenegado.emergencia.motivo')}
                value={motivo}
                maxLength={MOTIVO_EMERGENCIA_MAX}
                autoFocus
                aria-describedby="contador-motivo-emergencia"
                onChange={(e) => setMotivo(e.target.value)}
              />
              <p id="contador-motivo-emergencia" className="text-xs">
                {t('accesoDenegado.emergencia.contador', { actual: motivo.trim().length, min: MOTIVO_EMERGENCIA_MIN, max: MOTIVO_EMERGENCIA_MAX })}
              </p>
              {error ? <p role="alert">{error}</p> : null}
              <Button size="sm" variant="destructive" disabled={!motivoValido || enviando} onClick={() => void enviar()}>
                {t('accesoDenegado.emergencia.confirmar')}
              </Button>
            </div>
          ) : null}
        </AlertDescription>
      </Alert>
    </div>
  );
}
